import { Fragment, useEffect, useRef } from "react";
import styled from "@emotion/styled";

/**
 * CanvasHeroBackground
 * --------------------
 * Background animado generativo (canvas) para el hero/slider.
 *
 * Objetivo:
 * - Reemplazar video loop (sin cortes visibles)
 * - Movimiento infinito y orgánico
 * - Look cinematográfico dorado (similar al video original)
 * - Mejor performance y control visual
 *
 * Capas visuales (orden de render):
 * 1. Fondo base oscuro cálido
 * 2. Capas de humo dorado (fog)
 * 3. Partículas doradas flotantes
 * 4. Vignette oscura en el lado derecho (foco visual)
 */

const ContainerDiv = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;

  ${({ canvascss }) => `
    background: ${canvascss?.bg_color_mobile};

    @media only screen and (min-width: 992px) {
        background: ${canvascss?.bg_color_desktop};
    }
  `}
`;

const CanvasHeroBackground = ({ options }) => {
  //Des
  const { bg_color_mobile, bg_color_desktop } = options;
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    let width, height;
    let particles = [];
    let fogLayers = [];

    /**
     * =========================
     * CONFIGURACIÓN GLOBAL
     * =========================
     */
    const PARTICLE_COUNT = 180; // Cantidad de partículas (densidad visual)
    // Fog layers se controlan manualmente más abajo (velocidad, alpha, altura)

    /**
     * =========================
     * RESIZE HANDLER
     * =========================
     * Mantiene el canvas full-screen y responsive.
     */
    const resize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };

    window.addEventListener("resize", resize);
    resize();

    /**
     * =========================
     * PARTICLES (CHISPAS DORADAS)
     * =========================
     * Simulan pequeñas luces flotantes como en el video.
     * Movimiento horizontal lento + variación de tamaño y alpha.
     */
    class Particle {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * width;
        this.y = height * 0.55 + Math.random() * height * 0.45; // zona baja
        this.radius = Math.random() * 2.6 + 0.8;
        this.speed = Math.random() * 0.25 + 0.08;
        this.alpha = Math.random() * 0.7 + 0.25;
      }

      update() {
        this.x += this.speed;
        if (this.x > width + 20) this.reset();
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(230, 194, 139, ${this.alpha})`;
        ctx.fill();
      }
    }

    /**
     * =========================
     * FOG LAYERS (HUMO DORADO)
     * =========================
     * Capas de niebla con gradientes radiales.
     * Se mueven lentamente para dar sensación orgánica.
     */
    class Fog {
      constructor(speed, alpha, yOffset) {
        this.speed = speed;
        this.alpha = alpha;
        this.yOffset = yOffset;
        this.x = Math.random() * width;
      }

      update() {
        this.x += this.speed;
        if (this.x > width) this.x = -width;
      }

      draw() {
        const gradient = ctx.createRadialGradient(
          this.x,
          height * this.yOffset,
          100,
          this.x,
          height * this.yOffset,
          width
        );

        gradient.addColorStop(0, `rgba(255, 190, 120, ${this.alpha})`);
        gradient.addColorStop(0.5, `rgba(255, 170, 90, ${this.alpha * 0.6})`);
        gradient.addColorStop(1, "rgba(0,0,0,0)");

        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);
      }
    }

    /**
     * =========================
     * INICIALIZACIÓN
     * =========================
     */

    // Crear partículas
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      particles.push(new Particle());
    }

    // Crear capas de humo (distintas alturas y opacidades)
    fogLayers.push(new Fog(0.05, 0.12, 0.25));
    fogLayers.push(new Fog(0.03, 0.18, 0.35));
    fogLayers.push(new Fog(0.02, 0.22, 0.45));

    /**
     * =========================
     * LOOP DE ANIMACIÓN
     * =========================
     */
    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      /**
       * 1️⃣ FONDO BASE
       * Oscuro cálido (NO negro puro).
       * Controla el nivel general de oscuridad del canvas.
       */
      ctx.fillStyle = "#000"; // ~20% más oscuro que base anterior
      ctx.fillRect(0, 0, width, height);

      /**
       * 2️⃣ HUMO / FOG
       * Aporta atmósfera y profundidad cinematográfica.
       */
      fogLayers.forEach((fog) => {
        fog.update();
        fog.draw();
      });

      /**
       * 3️⃣ PARTÍCULAS
       * Chispas doradas que dan vida al fondo.
       */
      particles.forEach((p) => {
        p.update();
        p.draw();
      });

      /**
       * 4️⃣ VIGNETTE LATERAL DERECHO
       * Oscurece progresivamente el lado derecho (~40%).
       * Dirige la atención hacia el texto (lado izquierdo).
       */
      const rightGradient = ctx.createLinearGradient(width * 0.6, 0, width, 0);

      rightGradient.addColorStop(0, "rgba(0,0,0,0)");
      rightGradient.addColorStop(1, "rgba(0,0,0,0.4)");

      ctx.fillStyle = rightGradient;
      ctx.fillRect(0, 0, width, height);

      requestAnimationFrame(animate);
    };

    animate();

    /**
     * =========================
     * CLEANUP
     * =========================
     */
    return () => {
      window.removeEventListener("resize", resize);
    };
  }, []);

  /**
   * Canvas ocupa toda la sección y queda detrás del contenido.
   */
  return (
    <Fragment>
      <ContainerDiv
        className="cover-background"
        canvascss={{ bg_color_mobile, bg_color_desktop }}
      ></ContainerDiv>
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          zIndex: 0,
        }}
      />
    </Fragment>
  );
};

export default CanvasHeroBackground;
